<script setup lang="ts">
/** Two-factor sign-in: turn it on (QR + code), keep recovery codes, turn it off; admins can require it for everyone. */
import type { TotpSetup, TwoFactorStatus } from '@codot/contracts';
import qrcode from 'qrcode-generator';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { errorMessage } from '../../api/http';
import { twoFactorApi } from '../../api/two-factor.api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast';

/** `required`: shown as the only page while the organization requires it and this person hasn't turned it on. */
const props = defineProps<{ required?: boolean }>();
const { t } = useI18n();
const auth = useAuthStore();

const status = ref<TwoFactorStatus | null>(null);
const setup = ref<TotpSetup | null>(null);
const codes = ref<string[] | null>(null);
const code = ref('');
const password = ref('');
const mode = ref<'idle' | 'newCodes' | 'off'>('idle');
const busy = ref(false);
const error = ref<string | null>(null);
const copied = ref(false);

const load = async () => (status.value = await twoFactorApi.status().catch(() => null));
onMounted(load);

const qr = computed(() => {
  if (!setup.value) return '';
  const q = qrcode(0, 'M');
  q.addData(setup.value.otpauthUrl);
  q.make();
  return q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
});

async function run(work: () => Promise<void>): Promise<void> {
  busy.value = true;
  error.value = null;
  try {
    await work();
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    busy.value = false;
  }
}
const start = () => run(async () => void (setup.value = await twoFactorApi.setup()));
const confirm = () =>
  run(async () => {
    codes.value = (await twoFactorApi.enable(code.value.trim())).recoveryCodes;
    setup.value = null;
    code.value = '';
    await load();
  });
const regenerate = () =>
  run(async () => {
    codes.value = (await twoFactorApi.newRecoveryCodes(code.value.trim())).recoveryCodes;
    code.value = '';
    mode.value = 'idle';
    await load();
  });
const turnOff = () =>
  run(async () => {
    await twoFactorApi.disable(password.value, code.value.trim());
    mode.value = 'idle';
    code.value = password.value = '';
    toast(t('tfa.disabled'));
    await load();
    await auth.reload();
  });
/** Codes saved: done (on the "required" page this unlocks the CMS). */
async function savedCodes(): Promise<void> {
  codes.value = null;
  await auth.reload();
}
async function copyCodes(): Promise<void> {
  await navigator.clipboard.writeText(codes.value!.join('\n')).catch(() => undefined);
  copied.value = true;
}
function downloadCodes(): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([`Displaro recovery codes for ${auth.user?.email ?? ''}\n\n${codes.value!.join('\n')}\n`], { type: 'text/plain' }));
  a.download = 'displaro-recovery-codes.txt';
  a.click();
  URL.revokeObjectURL(a.href);
}

/* ---------------- the organization's requirement (admins) ---------------- */
const org = computed(() => auth.activeOrganization);
const canRequire = computed(() => !props.required && auth.can('ADMIN'));
async function toggleRequire(on: boolean): Promise<void> {
  await run(async () => {
    await twoFactorApi.setOrgSecurity(on);
    toast(on ? t('tfa.orgOn') : t('tfa.orgOff'));
    await auth.reload();
  });
}
</script>

<template>
  <section class="card space-y-4 p-4">
    <header class="flex flex-wrap items-center gap-2">
      <h2 class="font-semibold">{{ t('tfa.title') }}</h2>
      <span
        v-if="status"
        class="badge"
        :class="status.enabled ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'"
        >{{ status.enabled ? t('tfa.on') : t('tfa.off') }}</span
      >
    </header>
    <p class="text-sm text-slate-600 dark:text-slate-300">{{ t('tfa.intro') }}</p>

    <!-- Recovery codes, shown once -->
    <div v-if="codes" class="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
      <h3 class="font-medium">{{ t('tfa.codesTitle') }}</h3>
      <p class="text-sm">{{ t('tfa.codesHint') }}</p>
      <ul class="grid grid-cols-2 gap-1 font-mono text-sm sm:grid-cols-5" dir="ltr">
        <li v-for="c in codes" :key="c">{{ c }}</li>
      </ul>
      <div class="flex flex-wrap gap-2">
        <button type="button" class="btn-secondary" @click="copyCodes">{{ copied ? t('tfa.copied') : t('tfa.copy') }}</button>
        <button type="button" class="btn-secondary" @click="downloadCodes">{{ t('tfa.download') }}</button>
        <button type="button" class="btn-primary" @click="savedCodes">{{ t('tfa.saved') }}</button>
      </div>
    </div>

    <template v-else-if="status && !status.enabled">
      <button v-if="!setup" type="button" class="btn-primary" :disabled="busy" @click="start">{{ t('tfa.turnOn') }}</button>
      <form v-else class="space-y-3" @submit.prevent="confirm">
        <p class="text-sm">{{ t('tfa.scan') }}</p>
        <!-- eslint-disable-next-line vue/no-v-html -- an SVG we generate from our own data -->
        <div class="w-44 rounded bg-white p-2" v-html="qr" />
        <p class="text-xs text-slate-500">{{ t('tfa.manual') }}</p>
        <code class="block break-all rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-800" dir="ltr">{{ setup.secret }}</code>
        <label class="label" for="tfa-code">{{ t('tfa.code') }}</label>
        <input id="tfa-code" v-model="code" class="input max-w-40 text-center tracking-[0.3em]" inputmode="numeric" autocomplete="one-time-code" maxlength="6" required />
        <div class="flex gap-2">
          <button class="btn-primary" :disabled="busy || code.trim().length !== 6">{{ t('tfa.confirm') }}</button>
          <button type="button" class="btn-secondary" @click="setup = null">{{ t('tfa.cancel') }}</button>
        </div>
      </form>
    </template>

    <template v-else-if="status?.enabled">
      <p class="text-sm text-slate-500">{{ t('tfa.codesLeft', { n: status.recoveryCodesLeft }, status.recoveryCodesLeft) }}</p>
      <div v-if="mode === 'idle'" class="flex flex-wrap gap-2">
        <button type="button" class="btn-secondary" @click="mode = 'newCodes'">{{ t('tfa.newCodes') }}</button>
        <button v-if="!status.requiredBy.length" type="button" class="btn-danger" @click="mode = 'off'">{{ t('tfa.turnOff') }}</button>
      </div>
      <p v-if="status.requiredBy.length" class="text-xs text-slate-500">{{ t('tfa.requiredBy', { orgs: status.requiredBy.join(', ') }) }}</p>
      <form v-if="mode !== 'idle'" class="space-y-2" @submit.prevent="mode === 'off' ? turnOff() : regenerate()">
        <p class="text-sm">{{ mode === 'off' ? t('tfa.turnOffHint') : t('tfa.newCodesHint') }}</p>
        <template v-if="mode === 'off'">
          <label class="label" for="tfa-pw">{{ t('tfa.password') }}</label>
          <input id="tfa-pw" v-model="password" class="input max-w-xs" type="password" autocomplete="current-password" required />
        </template>
        <label class="label" for="tfa-code2">{{ t('tfa.code') }}</label>
        <input id="tfa-code2" v-model="code" class="input max-w-40" autocomplete="one-time-code" maxlength="20" required />
        <div class="flex gap-2">
          <button :class="mode === 'off' ? 'btn-danger' : 'btn-primary'" :disabled="busy">{{ mode === 'off' ? t('tfa.turnOff') : t('tfa.newCodes') }}</button>
          <button type="button" class="btn-secondary" @click="mode = 'idle'">{{ t('tfa.cancel') }}</button>
        </div>
      </form>
    </template>

    <p v-if="error" class="alert-error" role="alert">{{ error }}</p>

    <!-- Admins: require it for everyone -->
    <div v-if="canRequire && org" class="space-y-1 border-t border-slate-200 pt-4 dark:border-slate-800">
      <label class="flex items-center gap-2 font-medium">
        <input type="checkbox" class="accent-indigo-600" :checked="org.require2fa" :disabled="busy || (!org.require2fa && !status?.enabled)" @change="toggleRequire(($event.target as HTMLInputElement).checked)" />
        {{ t('tfa.orgTitle') }}
      </label>
      <p class="text-xs text-slate-500">{{ t('tfa.orgHint', { org: org.name }) }}</p>
      <p v-if="!org.require2fa && !status?.enabled" class="text-xs text-amber-700 dark:text-amber-400">{{ t('tfa.orgNeedsYours') }}</p>
    </div>
  </section>
</template>
